/**
 * Job Form Validation Utility
 * Validates job posting form data before submission
 */

export const validateJobForm = (formData) => {
  const errors = {};

  // Required fields validation
  if (!formData.title || formData.title.trim() === '') {
    errors.title = 'Job title is required';
  } else if (formData.title.length < 3) {
    errors.title = 'Job title must be at least 3 characters';
  } else if (formData.title.length > 200) {
    errors.title = 'Job title cannot exceed 200 characters';
  }

  // companyId is optional for consultants posting under their own name
  if (!formData.postUnderOwnName && (!formData.companyId || formData.companyId === '')) {
    errors.companyId = 'Please select a company';
  }

  if (!formData.description || formData.description.trim() === '') {
    errors.description = 'Job description is required';
  } else if (formData.description.length < 50) {
    errors.description = 'Job description must be at least 50 characters';
  }

  if (!formData.jobType || formData.jobType === '') {
    errors.jobType = 'Job type is required';
  }

  // Location validation
  if (!formData.location || !formData.location.city) {
    errors.location = 'Job location (city) is required';
  }
  
  // Address validation (optional but if provided should be valid)
  if (formData.location && formData.location.address && formData.location.address.trim() === '') {
    errors.address = 'Address cannot be empty if provided';
  }

  // Salary validation
  if (formData.salary) {
    if (formData.salary.min && formData.salary.max) {
      if (parseInt(formData.salary.min) > parseInt(formData.salary.max)) {
        errors.salary = 'Minimum salary cannot be greater than maximum salary';
      }
    }
    if (formData.salary.min && parseInt(formData.salary.min) < 0) {
      errors.salary = 'Salary cannot be negative';
    }
  }

  // Experience validation
  if (formData.experienceRequired) {
    if (formData.experienceRequired.min && formData.experienceRequired.max) {
      if (parseInt(formData.experienceRequired.min) > parseInt(formData.experienceRequired.max)) {
        errors.experience = 'Minimum experience cannot be greater than maximum experience';
      }
    }
    if (formData.experienceRequired.min && parseInt(formData.experienceRequired.min) < 0) {
      errors.experience = 'Experience cannot be negative';
    }
  }

  // Number of positions validation
  if (formData.numberOfPositions && parseInt(formData.numberOfPositions) < 1) {
    errors.numberOfPositions = 'Number of positions must be at least 1';
  }

  // Application deadline validation
  if (formData.applicationDeadline) {
    const deadline = new Date(formData.applicationDeadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (deadline < today) {
      errors.applicationDeadline = 'Application deadline cannot be in the past';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Clean form data before submission
 * Removes empty strings and null values for ObjectId fields
 */
export const cleanJobFormData = (formData) => {
  const cleaned = { ...formData };

  // Remove empty ObjectId fields
  const objectIdFields = ['educationRequired', 'shiftType', 'industryType', 'jobType'];
  objectIdFields.forEach(field => {
    if (cleaned[field] === '' || cleaned[field] === null || cleaned[field] === undefined) {
      delete cleaned[field];
    }
  });

  // Clean up skills arrays
  if (cleaned.requiredSkills && Array.isArray(cleaned.requiredSkills)) {
    cleaned.requiredSkills = cleaned.requiredSkills.filter(skill => skill && skill !== '');
  }
  if (cleaned.optionalSkills && Array.isArray(cleaned.optionalSkills)) {
    cleaned.optionalSkills = cleaned.optionalSkills.filter(skill => skill && skill !== '');
  }

  // Remove empty strings from nested objects
  if (cleaned.location) {
    Object.keys(cleaned.location).forEach(key => {
      if (cleaned.location[key] === '' || cleaned.location[key] === null) {
        delete cleaned.location[key];
      }
    });
  }

  if (cleaned.salary) {
    Object.keys(cleaned.salary).forEach(key => {
      if (cleaned.salary[key] === '' || cleaned.salary[key] === null) {
        delete cleaned.salary[key];
      }
    });
  }

  if (cleaned.experienceRequired) {
    Object.keys(cleaned.experienceRequired).forEach(key => {
      if (cleaned.experienceRequired[key] === '' || cleaned.experienceRequired[key] === null) {
        delete cleaned.experienceRequired[key];
      }
    });
  }

  return cleaned;
};

/**
 * Get user-friendly error message
 */
export const getJobErrorMessage = (error) => {
  if (!error) return 'Failed to create job. Please try again.';

  // Handle specific error codes
  if (error.code === 'VALIDATION_ERROR') {
    if (error.message.includes('Cast to ObjectId')) {
      return 'Invalid selection for one or more fields. Please check your form and try again.';
    }
    if (error.message.includes('educationRequired')) {
      return 'Invalid education requirement selected. Please select a valid option.';
    }
    if (error.message.includes('jobType')) {
      return 'Invalid job type selected. Please select a valid option.';
    }
    if (error.message.includes('shiftType')) {
      return 'Invalid shift type selected. Please select a valid option.';
    }
    if (error.message.includes('industryType')) {
      return 'Invalid industry type selected. Please select a valid option.';
    }
    return error.message || 'Please check all required fields and try again.';
  }

  if (error.code === 'AUTHORIZATION_ERROR') {
    // Company verification errors
    if (error.message.includes('under verification')) {
      return 'Your company is under verification. You can post jobs once it is approved by admin.';
    }
    if (error.message.includes('create your company profile')) {
      return 'Please create your company profile first before posting jobs.';
    }
    if (error.message.includes('rejected')) {
      return error.message; // Show rejection reason from backend
    }
    if (error.message.includes('documents')) {
      return 'Additional documents are required for company verification. Please check your email or contact admin.';
    }
    if (error.message.includes('company')) {
      return error.message;
    }
    return 'You are not authorized to post jobs. Please verify your company profile.';
  }

  if (error.code === 'NOT_FOUND') {
    return 'Company not found. Please select a valid company.';
  }

  if (error.code === 'DUPLICATE_ENTRY') {
    if (error.message.includes('slug') || error.message.includes('title')) {
      return error.message || 'A job with this title already exists. Please try a different title.';
    }
    return error.message || 'Duplicate entry detected. Please check your data.';
  }

  // Default message
  return error.message || 'Failed to create job. Please try again.';
};
